import queryString from 'query-string';
import { delay } from 'redux-saga';
import {
  call,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { constants, stepUtils } from '@frontend/partviewer';

import {
  PROCESSING_CATEGORIES,
  UNITS,
  VECTOR_FILE_EDITOR_MODES,
} from 'constants/all';

import partService from 'services/partService';

import { PAGE_REQUEST } from 'store/app/types';

import { inchesToMm } from 'utils/helpers';
import { isStatusInProgress } from 'utils/partUtils';

import { EDITOR_MODES } from '../constants';
import {
  makeDxf,
  measurementTool,
  selectorTool,
  types,
  updateContour,
  vectorEditor,
} from './actions';
import {
  getActiveEntityId,
  getDisplayUnits,
  getPresentExcludedEdgeKeys,
  getHideExcluded,
  getMode,
  getPartFileUuid,
  getPartMasterVersion,
  getPartUuid,
  getScale,
  getVectorizationStatus,
} from './reducer';

const { COLOR_CODES, ENTITY_TYPES, OPACITY_MAP, PART_TYPES, SPLIT_CHAR } =
  constants;

const { constructEntityName } = stepUtils;

const getEmptyPartModel = () => {
  return {
    assemblies: [],
    assemblyInstances: [],
    bodies: [],
    parts: [],
  };
};

export default function* root() {
  yield takeLatest(types.RECEIVE_DISPLAY_UNITS, watchReceiveDisplayUnits);
  yield takeLatest(types.RECEIVE_SCALE, watchReceiveScale);
  yield takeLatest(
    types.CONTOUR_SELECTOR_RECEIVE_EXCLUDED_KEYS,
    watchReceiveExcludedEdgeKeys,
  );
  yield takeLatest(
    types.CONTOUR_SELECTOR_RECEIVE_HIDE_EXCLUDED,
    watchReceiveHideExcluded,
  );
  yield takeLatest(types.MAKE_DXF_REQUEST, watchMakeDxfRequest);
  yield takeLatest(types.UPDATE_CONTOUR_REQUEST, watchUpdateContourRequest);
  yield takeEvery(types.CONTOUR_SELECTOR_UNDO, watchUndo);
  yield takeEvery(types.CONTOUR_SELECTOR_REDO, watchRedo);
  yield spawn(pageWorker);
}

function* pageWorker() {
  // restart "periodicallyCheckStatus" saga when
  // PAGE_REQUEST is dispatched
  for (;;) {
    yield race({
      vectorEditor: call(periodicallyCheckStatus),
      otherPage: take(PAGE_REQUEST),
    });
  }
}

function* periodicallyCheckStatus() {
  yield take(types.INIT_VECTOR_EDITOR);
  while (true) {
    try {
      const oldStatus = yield select(getVectorizationStatus);
      if (isStatusInProgress(oldStatus)) {
        const partFileUuid = yield select(getPartFileUuid);
        const response = yield call(partService.checkStatuses, [partFileUuid]);
        const nextStatus =
          response.statuses.processing[0][PROCESSING_CATEGORIES.VECTORIZATION]
            .status;
        if (nextStatus !== oldStatus) {
          yield call(handleStatusChangeToFinished);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      yield call(delay, 2000);
    }
  }
}

function* constructPartModelFromPageVectorization({ pageVectorization }) {
  const model = getEmptyPartModel();
  if (pageVectorization.url) {
    const fileContent = yield fetch(pageVectorization.url).then((response) =>
      response.json(),
    );

    const newPart = {
      //uuid: pageVectorization.partFileUuid,
      partFileUuid: pageVectorization.partFileUuid,
      name: pageVectorization.partFileUuid,
      tess: {
        Type: PART_TYPES.DXF,
        Faces: [],
        Edges: fileContent,
        Vertexes: [],
      },
    };

    const newBody = {
      id: pageVectorization.id.toString(),
      name: pageVectorization.id.toString(),
      partFileUuid: pageVectorization.partFileUuid,
      isRoot: true,
      transform: null,
      color: null,
      opacity: 1.0,
    };

    model.parts.push(newPart);
    model.bodies.push(newBody);
  }

  return model;
}

function* constructPartModelFromContourTracker({ contourTracker }) {
  const model = getEmptyPartModel();
  if (contourTracker.url) {
    const fileContent = yield fetch(contourTracker.url).then((response) =>
      response.json(),
    );

    const newPart = {
      //uuid: contourTracker.partFileUuid,
      partFileUuid: contourTracker.partFileUuid,
      name: contourTracker.partFileUuid,
      tess: {
        Type: PART_TYPES.DXF,
        Faces: [],
        Edges: contourTracker.createdFromPageVectorization
          ? fileContent
          : fileContent.Edges,
        Vertexes: [],
      },
    };

    const newBody = {
      id: contourTracker.id.toString(),
      name: contourTracker.id.toString(),
      partFileUuid: contourTracker.partFileUuid,
      isRoot: true,
      transform: null,
      color: null,
      opacity: 1.0,
    };

    model.parts.push(newPart);
    model.bodies.push(newBody);
  }

  return model;
}

const constructInitPayload = ({ obj }) => {
  const presentKeys = obj.excludedEdgeIndices.map((i) =>
    constructEntityName(obj.id, ENTITY_TYPES.EDGE, i),
  );
  return {
    activeEntityId: obj.id,
    displayUnits: obj.displayUnits,
    excludedEdgeKeys: {
      past: [],
      present: presentKeys,
      future: [],
    },
    partFileUuid: obj.partFileUuid,
    scale: obj.scale,
    totalEdgeCount: obj.totalEdgeCount,
    vectorizationStatus: obj.status[PROCESSING_CATEGORIES.VECTORIZATION].status,
  };
};

const collectHighlight = ({ excludedEdgeKeys, hideExcluded }) => {
  const highlight = [];
  for (const key of excludedEdgeKeys) {
    const pieces = key.split(SPLIT_CHAR);
    const bodyId = pieces[0];
    const entityType = pieces[1];
    const edgeIndex = pieces[2];
    highlight.push({
      body: bodyId,
      type: entityType,
      color: COLOR_CODES.EXCLUDED_EDGE,
      opacity: OPACITY_MAP.EXCLUDED_EDGE,
      visibility: !hideExcluded,
      indices: [edgeIndex],
    });
  }
  return highlight;
};

function* handleStatusChangeToFinished() {
  const mode = yield select(getMode);
  const activeId = yield select(getActiveEntityId);

  let model = getEmptyPartModel();
  let payload = {};
  if (mode === VECTOR_FILE_EDITOR_MODES.VECTORIZE) {
    const pageVectorization = yield call(
      partService.getPageVectorization,
      activeId,
    );

    payload = constructInitPayload({ obj: pageVectorization });
    model = yield call(constructPartModelFromPageVectorization, {
      pageVectorization,
    });
  }

  const highlight = collectHighlight({
    excludedEdgeKeys: payload.excludedEdgeKeys.present,
    hideExcluded: true,
  });

  yield put(vectorEditor.receiveViewerData({ viewerData: model }));
  yield put(measurementTool.enable());
  yield put(vectorEditor.initFromStatusChange(payload));
  yield put(vectorEditor.receiveHighlight({ highlight }));
}

export function* initVectorFileEditor({ match }) {
  yield put(vectorEditor.reset());

  const { mode, activeId } = match.params;

  const { part: partUuid, version: partMasterVersion } = queryString.parse(
    window.location.search,
  );

  let model = getEmptyPartModel();
  let payload = {};

  if (mode === VECTOR_FILE_EDITOR_MODES.VECTORIZE) {
    const pageVectorization = yield call(
      partService.getPageVectorization,
      activeId,
    );

    payload = constructInitPayload({ obj: pageVectorization });
    model = yield call(constructPartModelFromPageVectorization, {
      pageVectorization,
    });
  } else {
    const contourTracker = yield call(partService.getContourTracker, activeId);

    payload = constructInitPayload({ obj: contourTracker });
    model = yield call(constructPartModelFromContourTracker, {
      contourTracker,
    });
  }

  const highlight = collectHighlight({
    excludedEdgeKeys: payload.excludedEdgeKeys.present,
    hideExcluded: true,
  });

  yield put(vectorEditor.receiveDisplayScale({ displayScale: payload.scale }));
  yield put(measurementTool.enable());
  yield put(vectorEditor.receiveViewerData({ viewerData: model }));
  yield put(
    vectorEditor.init({
      ...payload,
      mode,
      partMasterVersion,
      partUuid,
    }),
  );
  yield put(vectorEditor.receiveHighlight({ highlight }));
}

function* watchReceiveDisplayUnits({ payload }) {
  // update the page vectorization model inline optimistically if in vectorization mode
  const mode = yield select(getMode);
  if (mode === VECTOR_FILE_EDITOR_MODES.VECTORIZE) {
    const pageVectorizationId = yield select(getActiveEntityId);
    try {
      yield call(partService.updatePageVectorization, pageVectorizationId, {
        displayUnits: payload.displayUnits,
      });
    } catch (error) {
      console.error(error);
    }
  }
}

function* watchReceiveScale({ payload }) {
  yield put(vectorEditor.receiveDisplayScale({ displayScale: payload.scale }));
  yield put(
    vectorEditor.receiveEditorMode({
      editorMode: EDITOR_MODES.CONTOUR_SELECTION,
    }),
  );
  yield put(measurementTool.disable());
  yield put(selectorTool.enable());

  // update the page vectorization model inline optimistically if in vectorization mode
  const mode = yield select(getMode);
  if (mode === VECTOR_FILE_EDITOR_MODES.VECTORIZE) {
    const pageVectorizationId = yield select(getActiveEntityId);
    try {
      yield call(partService.updatePageVectorization, pageVectorizationId, {
        scale: payload.scale,
      });
    } catch (error) {
      console.error(error);
    }
  }
}
function* finishEdgeKeyUpdate({ edgeKeys }) {
  const hideExcluded = yield select(getHideExcluded);

  const highlight = collectHighlight({
    excludedEdgeKeys: edgeKeys,
    hideExcluded,
  });
  yield put(selectorTool.clearAllSelections());
  yield put(vectorEditor.receiveHighlight({ highlight }));

  // update the page vectorization model inline optimistically if in vectorization mode
  const mode = yield select(getMode);
  if (mode === VECTOR_FILE_EDITOR_MODES.VECTORIZE) {
    const pageVectorizationId = yield select(getActiveEntityId);
    try {
      yield call(partService.updatePageVectorization, pageVectorizationId, {
        excludedEdgeIndices: edgeKeys.map((ek) =>
          parseInt(ek.split(SPLIT_CHAR)[2]),
        ),
      });
    } catch (error) {
      console.error(error);
    }
  }
}

function* watchReceiveExcludedEdgeKeys({ payload }) {
  const { edgeKeys } = payload;
  yield call(finishEdgeKeyUpdate, { edgeKeys });
}

function* watchUndo() {
  const edgeKeys = yield select(getPresentExcludedEdgeKeys);
  yield call(finishEdgeKeyUpdate, { edgeKeys });
}

function* watchRedo() {
  const edgeKeys = yield select(getPresentExcludedEdgeKeys);
  yield call(finishEdgeKeyUpdate, { edgeKeys });
}

function* watchReceiveHideExcluded({ payload }) {
  const { hideExcluded } = payload;
  const excludedEdgeKeys = yield select(getPresentExcludedEdgeKeys);

  const highlight = collectHighlight({ excludedEdgeKeys, hideExcluded });
  yield put(vectorEditor.receiveHighlight({ highlight }));
}

function* watchMakeDxfRequest({ payload }) {
  const { thickness } = payload;
  const displayUnits = yield select(getDisplayUnits);
  // thickness must post to the backend in MM
  const useThickness =
    displayUnits === UNITS.MM ? thickness : inchesToMm(thickness);

  const excludedEdgeKeys = yield select(getPresentExcludedEdgeKeys);
  const excludedEdgeIndices = excludedEdgeKeys.map((ek) =>
    parseInt(ek.split(SPLIT_CHAR)[2]),
  );

  const pageVectorizationId = yield select(getActiveEntityId);
  const partUuid = yield select(getPartUuid);
  const partMasterVersion = yield select(getPartMasterVersion);

  const data = {
    excludedEdgeIndices,
    partMasterVersion,
    partUuid,
    thickness: useThickness,
  };

  try {
    yield call(
      partService.createDxfFileFromPageVectorization,
      pageVectorizationId,
      data,
    );
    yield put(makeDxf.success());
  } catch (error) {
    yield put(makeDxf.error(error));
  }
}

function* watchUpdateContourRequest() {
  const displayUnits = yield select(getDisplayUnits);
  const excludedEdgeKeys = yield select(getPresentExcludedEdgeKeys);
  const excludedEdgeIndices = excludedEdgeKeys.map((ek) =>
    parseInt(ek.split(SPLIT_CHAR)[2]),
  );
  const scale = yield select(getScale);
  const contourTrackerId = yield select(getActiveEntityId);
  const partUuid = yield select(getPartUuid);
  const partMasterVersion = yield select(getPartMasterVersion);

  const data = {
    displayUnits: displayUnits,
    excludedEdgeIndices,
    partMasterVersion,
    partUuid,
    scale,
  };

  try {
    yield call(partService.updateContourRequest, contourTrackerId, data);
    yield put(updateContour.success());
  } catch (error) {
    yield put(updateContour.error(error));
  }
}
